/**
 * DEFCAT-R2-06 / R2-07 — i18n row editor for catalogue-entry LocalizedString
 * fields (name, abbreviation, defaultDescription).
 *
 * UX is copied from the external-IDs editor on Assets → Turbines → Edit Turbine
 * (`apps/web/src/features/.../ExternalIdsEditor.tsx`): an inline repeating list
 * of rows, each `[language picker | value text field | ⊖ remove]`, with a
 * "+ Add language" button appending a row.
 *
 * Rules (OQ-R2-4):
 *   - The English (`en`) row is ALWAYS present and CANNOT be removed (no ⊖).
 *   - For `name`, the en value is required (enforced by the caller via
 *     `i18nRowsToMap` + a non-empty check).
 *   - For `abbreviation`, the en value may be empty / absent — no abbreviation
 *     at all is valid (the caller decides whether to PATCH null vs a map).
 *   - Only rows with a non-empty value are stored — the JSONB map sent on PATCH
 *     contains exactly those rows (`i18nRowsToMap`).
 *   - `en` is excluded from the picker (it is always the locked first row);
 *     a curated language list plus any free ISO-639-1 code is offered.
 */
import { type ReactElement } from "react";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { typography } from "@aeriosiq/ui";
import type { LocalizedString } from "@aeriosiq/types";
import { CircleMinusIcon, PlusIcon } from "@aeriosiq/ui/icons";

/**
 * Section-heading style matching the analysis filter widget's `LevelHeader`
 * (the platform's canonical compact-widget scale).
 */
const sectionHeadingSx = {
  fontSize: typography.size["label-md"],
  fontWeight: typography.weight.semibold,
  letterSpacing: typography.tracking["label-md"],
  color: "text.secondary",
  textTransform: "uppercase" as const,
  display: "block",
  mb: 0.75,
};

/** Dense field font — caption-lg (13), filter-widget body scale. */
const compactFieldSx = {
  "& .MuiInputBase-input": { fontSize: typography.size["caption-lg"] },
};

export interface I18nRow {
  /** ISO-639-1 language code, lower-case (e.g. "en", "de"). */
  lang: string;
  value: string;
}

/** Curated language list offered first in the picker (en is always locked). */
export const CURATED_LANGUAGES: { code: string; label: string }[] = [
  { code: "de", label: "German (de)" },
  { code: "es", label: "Spanish (es)" },
  { code: "fr", label: "French (fr)" },
  { code: "it", label: "Italian (it)" },
  { code: "nl", label: "Dutch (nl)" },
  { code: "da", label: "Danish (da)" },
  { code: "sv", label: "Swedish (sv)" },
  { code: "pt", label: "Portuguese (pt)" },
  { code: "pl", label: "Polish (pl)" },
];

/**
 * Convert a LocalizedString JSONB map into editor rows. `en` is hoisted to the
 * first (locked) row even when absent/empty, so the editor always shows it.
 */
export function i18nMapToRows(
  map: LocalizedString | null | undefined,
): I18nRow[] {
  const rows: I18nRow[] = [{ lang: "en", value: map?.en ?? "" }];
  if (map) {
    for (const key of Object.keys(map)) {
      if (key === "en") continue;
      const v = map[key];
      if (typeof v === "string") rows.push({ lang: key, value: v });
    }
  }
  return rows;
}

/**
 * Fold editor rows back into a LocalizedString map for the PATCH body. Only
 * rows with a non-empty (trimmed) value are kept; an empty `en` collapses to
 * `{ en: "" }` only if every row is empty — callers decide null-vs-map.
 */
export function i18nRowsToMap(rows: I18nRow[]): LocalizedString {
  const out: Record<string, string> = {};
  for (const r of rows) {
    const lang = r.lang.trim().toLowerCase();
    const value = r.value.trim();
    if (lang.length === 0 || value.length === 0) continue;
    out[lang] = value;
  }
  // The contract's LocalizedString always carries `en`; keep it present even
  // when empty so the shape is stable. Callers that want "no value at all"
  // (abbreviation) send null instead.
  if (!("en" in out)) out.en = "";
  return out as LocalizedString;
}

/** True when no row carries a non-empty value (used for abbreviation null-clear). */
export function i18nRowsEmpty(rows: I18nRow[]): boolean {
  return rows.every((r) => r.value.trim().length === 0);
}

export interface I18nRowEditorProps {
  label: string;
  value: I18nRow[];
  onChange: (next: I18nRow[]) => void;
  disabled?: boolean;
  /** Multiline value inputs (defaultDescription). */
  multiline?: boolean;
  /** Helper text under the field group. */
  helperText?: string;
}

export function I18nRowEditor({
  label,
  value,
  onChange,
  disabled = false,
  multiline = false,
  helperText,
}: I18nRowEditorProps): ReactElement {
  // Languages already used (so the picker doesn't offer duplicates).
  const usedLangs = new Set(value.map((r) => r.lang.toLowerCase()));

  function updateValue(index: number, next: string): void {
    const rows = [...value];
    const current = rows[index];
    if (!current) return;
    rows[index] = { ...current, value: next };
    onChange(rows);
  }

  function updateLang(index: number, next: string): void {
    const rows = [...value];
    const current = rows[index];
    if (!current || current.lang === "en") return; // en is locked
    rows[index] = { ...current, lang: next.trim().toLowerCase() };
    onChange(rows);
  }

  function remove(index: number): void {
    const current = value[index];
    if (!current || current.lang === "en") return; // en cannot be removed
    onChange(value.filter((_, i) => i !== index));
  }

  function append(): void {
    // Pick the first curated language not yet used; fall back to empty (the
    // user types a code).
    const free = CURATED_LANGUAGES.find((l) => !usedLangs.has(l.code));
    onChange([...value, { lang: free?.code ?? "", value: "" }]);
  }

  return (
    <Box>
      <Typography component="span" sx={sectionHeadingSx}>
        {label}
      </Typography>
      <Stack spacing={multiline ? 1 : 0.5}>
        {value.map((row, index) => {
          const isEn = row.lang === "en";
          const langField = isEn ? (
            <TextField
              value="English (en)"
              size="small"
              sx={{ width: 180, ...compactFieldSx }}
              disabled
              slotProps={{
                htmlInput: {
                  "aria-label": `${label} language 1 (English, required)`,
                },
              }}
            />
          ) : (
            <Autocomplete
              freeSolo
              size="small"
              sx={{ width: 180, ...compactFieldSx }}
              disabled={disabled}
              options={CURATED_LANGUAGES.filter(
                (l) => l.code === row.lang || !usedLangs.has(l.code),
              ).map((l) => l.code)}
              value={row.lang}
              onChange={(_e, next) => {
                updateLang(index, next ?? "");
              }}
              onInputChange={(_e, next) => {
                updateLang(index, next);
              }}
              renderInput={(params) => {
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                const passThrough: any = params;
                return (
                  <TextField
                    {...passThrough}
                    placeholder="lang"
                    slotProps={{
                      htmlInput: {
                        ...params.inputProps,
                        "aria-label": `${label} language ${String(index + 1)}`,
                      },
                    }}
                  />
                );
              }}
            />
          );
          const valueField = (
            <TextField
              value={row.value}
              onChange={(e) => {
                updateValue(index, e.target.value);
              }}
              placeholder={isEn ? "English value" : "Value"}
              size="small"
              sx={{ flex: 1, ...compactFieldSx }}
              disabled={disabled}
              multiline={multiline}
              {...(multiline ? { minRows: 2 } : {})}
              slotProps={{
                htmlInput: {
                  "aria-label": `${label} value ${String(index + 1)} (${row.lang || "lang"})`,
                },
              }}
            />
          );
          const removeButton =
            !isEn && !disabled ? (
              <IconButton
                aria-label={`Remove ${label} language ${String(index + 1)}`}
                size="small"
                sx={{ p: 0.5 }}
                onClick={() => {
                  remove(index);
                }}
              >
                <CircleMinusIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : (
              <Box sx={{ width: 28, flexShrink: 0 }} />
            );
          // Multiline values (default description): VERTICAL arrangement —
          // language picker (+ remove) on top, the full-width text field
          // below (Christian, 2026-06-04: "looks much cleaner"). Single-line
          // values keep the compact horizontal row.
          return multiline ? (
            <Stack key={isEn ? "en" : `row-${String(index)}`} spacing={0.5}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {langField}
                {removeButton}
              </Stack>
              {valueField}
            </Stack>
          ) : (
            <Stack
              key={isEn ? "en" : `row-${String(index)}`}
              direction="row"
              spacing={0.5}
              alignItems="flex-start"
            >
              {langField}
              {valueField}
              {removeButton}
            </Stack>
          );
        })}
      </Stack>
      {!disabled ? (
        <Button
          size="small"
          variant="text"
          startIcon={<PlusIcon sx={{ fontSize: 16 }} />}
          onClick={append}
          sx={{ mt: 0.75, fontSize: typography.size["caption-lg"] }}
          aria-label={`Add ${label} language`}
        >
          Add language
        </Button>
      ) : null}
      {helperText ? (
        <Typography
          color="text.secondary"
          sx={{ display: "block", mt: 0.5, fontSize: 11 }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}
